import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../../core/services/api.service';
import { IssueApiService } from '../issues/issue.service';
import { EpicProgressDto } from '../issues/issue.models';

interface ProjectOption { id: string; name: string; }

@Component({
  selector: 'app-epics',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    MatCardModule, MatFormFieldModule, MatSelectModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatProgressBarModule, MatTooltipModule
  ],
  templateUrl: './epics.component.html',
  styleUrl: './epics.component.scss'
})
export class EpicsComponent implements OnInit, OnDestroy {
  private readonly issuesApi = inject(IssueApiService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectOption[]>([]);
  readonly epics = signal<EpicProgressDto[]>([]);

  projectId = '';

  ngOnInit(): void {
    this.api.getPaged<ProjectOption>('v1/projects', { page: 1, pageSize: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          const items = page.items ?? [];
          this.projects.set(items.map(p => ({ id: p.id, name: p.name })));
          if (items.length && !this.projectId) {
            this.projectId = items[0].id;
            this.loadEpics();
          }
        },
        error: () => this.error.set('Failed to load projects.')
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadEpics(): void {
    if (!this.projectId) {
      this.epics.set([]);
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.issuesApi.listEpics(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (list) => {
        this.epics.set(list ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load epics.');
        this.loading.set(false);
      }
    });
  }

  openEpic(epic: EpicProgressDto): void {
    this.router.navigate(['/issues', epic.epicId]);
  }
}
